import { useState } from 'react'
import { useScrollSpy } from '../utils/dom'
import { cx } from '../utils/strings'
import CtaLink, { buttonStyles } from './ui/cta-link'
import Collapsible from './ui/collapsible'
import AngleIcon from '@/icons/bwi/angle-up.svg'
import { useTranslations } from '@/utils/locale.ts'

export type TocItem = {
  title: string
  level: number
  hash: string
}

type TocNavProps = {
  id?: string
  sections?: TocItem[]
}

export default function TocNav({ id = 'toc-nav', sections = [] }: TocNavProps) {
  if (sections) useScrollSpy({ id })
  const [isOpen, setIsOpen] = useState(false)
  const { t } = useTranslations()

  if (!sections || sections.length === 0) return null

  const renderSections = () => {
    return (
      <ul className={'px-4 py-2 list-none md:p-0'}>
        {sections.map((section) => (
          <li
            key={section.hash}
            className={cx(
              'rounded-sm [&.scrollactive]:bg-secondary-100 hover:bg-secondary-100 transition-colors duration-300 mb-1',
              section.level === 3 && 'ml-3',
              section.level === 4 && 'ml-5',
              section.level >= 5 && 'ml-7'
            )}
          >
            <CtaLink
              style={buttonStyles.btnText}
              to={section.hash}
              className={cx('font-normal text-secondary-800 dark:text-secondary-50 leading-normal px-3 block! py-1')}
            >
              {section.title}
            </CtaLink>
          </li>
        ))}
      </ul>
    )
  }

  return (
    <nav id={id} className="mt-3 md:my-6">
      <div className="hidden md:block">
        <h6 className="block mb-4 uppercase text-xl">{t('On this page')}</h6>
        {renderSections()}
      </div>

      <div className="md:hidden border bg-secondary-50 dark:bg-secondary-800 rounded-xl">
        <button
          className={cx(
            'flex w-full justify-between text-primaryBlue py-2 px-4 font-semibold text-sm',
            isOpen && 'shadow-xs'
          )}
          type="button"
          onClick={() => setIsOpen(!isOpen)}
        >
          <span>{t('On this page')}</span>
          <AngleIcon className={cx('ml-3 w-4 shrink-0 mt-1 h-3 transition duration-200', isOpen ? '' : `rotate-180`)} />
        </button>

        <Collapsible isOpen={isOpen}>{renderSections()}</Collapsible>
      </div>
    </nav>
  )
}
